import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "row", "keepSelected"]

  connect() {
    this.boundRefreshLimits = this.refreshLimits.bind(this)
    document.addEventListener(
      "activation-wizard:role-counters-updated",
      this.boundRefreshLimits
    )
    this.refreshLimits()
  }

  disconnect() {
    document.removeEventListener(
      "activation-wizard:role-counters-updated",
      this.boundRefreshLimits
    )
    if (this.keepSelectedTimerIds?.length) {
      this.keepSelectedTimerIds.forEach((id) => clearTimeout(id))
      this.keepSelectedTimerIds = []
    }
  }

  readSlotState() {
    const root = document.querySelector(".edit-user-role")
    const rawMax = root?.getAttribute(
      "data-activation-wizard-maximum-count-value"
    )
    const numericMax = Number(rawMax)
    const isUnlimited =
      rawMax === null ||
      rawMax === undefined ||
      rawMax === "" ||
      rawMax === "null" ||
      Number.isNaN(numericMax) ||
      numericMax <= 0
    const maxCount = isUnlimited ? Infinity : numericMax
    const actualRecords = document.querySelectorAll(
      ".nca-tbody .actual-record"
    ).length
    const remaining = isUnlimited
      ? Infinity
      : Math.max(0, maxCount - actualRecords)

    return { isUnlimited, remaining, maxCount, actualRecords }
  }

  refreshLimits() {
    if (!this.hasSelectAllTarget) return

    const { isUnlimited, remaining } = this.readSlotState()
    const rows = this.rowTargets
    const n = rows.length
    const master = this.selectAllTarget

    this.enforceCap()

    if (!isUnlimited && remaining <= 0) {
      rows.forEach((cb) => {
        cb.checked = false
        cb.disabled = true
      })
    } else {
      rows.forEach((cb) => {
        cb.disabled = false
      })
    }

    if (n === 0) {
      master.disabled = true
    } else if (!isUnlimited && remaining <= 0) {
      master.disabled = true
    } else if (!isUnlimited && remaining < n) {
      master.disabled = true
    } else {
      master.disabled = false
    }

    const selectAllLabel = master.closest("label")
    if (selectAllLabel) {
      selectAllLabel.classList.toggle("text-muted", master.disabled)
      selectAllLabel.style.cursor = master.disabled ? "not-allowed" : "pointer"
    }

    if (this.hasKeepSelectedTarget) {
      const checkedCount = rows.filter((cb) => cb.checked).length
      const over =
        !isUnlimited && checkedCount > remaining
      this.keepSelectedTarget.disabled =
        (!isUnlimited && remaining <= 0) ||
        checkedCount === 0 ||
        over
    }

    this.updateMasterCheckState()
  }

  enforceCap() {
    const { isUnlimited, remaining } = this.readSlotState()
    if (isUnlimited) return

    let checked = this.rowTargets.filter((cb) => cb.checked)
    while (checked.length > remaining) {
      checked[checked.length - 1].checked = false
      checked = this.rowTargets.filter((cb) => cb.checked)
    }
  }

  toggleSelectAll(event) {
    const master = event.currentTarget
    if (master.disabled) return

    master.indeterminate = false
    const on = master.checked
    this.rowTargets.forEach((cb) => {
      if (!cb.disabled) cb.checked = on
    })
    this.refreshLimits()
  }

  keepSelected(event) {
    event.preventDefault()

    const { remaining, isUnlimited } = this.readSlotState()
    if (!isUnlimited && remaining <= 0) return

    if (this.hasKeepSelectedTarget && this.keepSelectedTarget.disabled) return

    const checked = this.rowTargets.filter((cb) => cb.checked)
    if (checked.length === 0) return

    if (!isUnlimited && checked.length > remaining) {
      window.alert(
        `Only ${remaining} open slot(s) remain. Reduce your selection or remove someone from New assignments first.`
      )
      return
    }

    const toProcess = isUnlimited ? checked : checked.slice(0, remaining)

    if (this.keepSelectedTimerIds?.length) {
      this.keepSelectedTimerIds.forEach((id) => clearTimeout(id))
    }
    this.keepSelectedTimerIds = []
    toProcess.forEach((cb, index) => {
      const timerId = window.setTimeout(() => {
        const link = cb.closest("tr")?.querySelector("a.keep-as-it-is")
        link?.click()
      }, index * 200)
      this.keepSelectedTimerIds.push(timerId)
    })
  }

  rowChanged(event) {
    const { remaining, isUnlimited } = this.readSlotState()
    if (!isUnlimited && event.currentTarget.checked) {
      const checked = this.rowTargets.filter((cb) => cb.checked)
      if (checked.length > remaining) {
        event.currentTarget.checked = false
        window.alert(
          `Only ${remaining} open slot(s) remain on the right. Uncheck others or free a slot before selecting more.`
        )
      }
    }
    this.refreshLimits()
  }

  rowTargetConnected() {
    this.refreshLimits()
  }

  rowTargetDisconnected() {
    this.refreshLimits()
  }

  updateMasterCheckState() {
    if (!this.hasSelectAllTarget) return

    const rows = this.rowTargets
    const n = rows.length
    const master = this.selectAllTarget

    if (n === 0) {
      master.checked = false
      master.indeterminate = false
      return
    }

    const checkedCount = rows.filter((cb) => cb.checked).length
    master.indeterminate = checkedCount > 0 && checkedCount < n
    master.checked = checkedCount === n
  }
}
